import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SepoDevice } from '@deprecated/api-interfaces';
import { Notification, NotificationService, NotificationType } from '@ird/ng-base';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AuthService } from '../../auth/service/auth.service';
import { DeviceRepository } from '../repositories/device.repository';
import { KpiLogsService } from './kpi-logs.service';
import { TenantConfigService } from './tenant-config.service';
import { WarrantyService } from './warranty.service';

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  // Device behavior subject
  private device$ = new BehaviorSubject<SepoDevice>(null);
  /**
   * Device changed observable
   */
  public deviceChanged = this.device$.asObservable();
  private deviceId: string;

  constructor(
    private deviceRepository: DeviceRepository,
    private authService: AuthService,
    private kpiLogsService: KpiLogsService,
    private warrantyService: WarrantyService,
    private tenantConfigService: TenantConfigService,
    private router: Router,
    private notificationService: NotificationService,
  ) {}

  /**
   * Sets the device ID and gets the device from the server
   *
   * @param deviceId device ID
   */
  setDeviceId(deviceId: string): void {
    this.deviceId = deviceId;
    localStorage.removeItem('deviceId');
    localStorage.setItem('deviceId', deviceId);
  }

  /**
   * Returns the device id, when it was set
   *
   * @returns ID of device
   */
  public getDeviceId(): string {
    return this.deviceId ?? localStorage.getItem('deviceId');
  }

  getDevice(deviceId: string): Promise<SepoDevice> {
    return new Promise((resolve, reject) => {
      this.deviceRepository.getDevice(deviceId).subscribe({
        next: (device) => {
          if (device) {
            this.device$.next(device);
            resolve(device);
          } else {
            reject();
          }
        },
        error: (err) => {
          this.device$.next(null);
          reject(err);
        },
      });
    });
  }

  getDeviceWithoutUpdating(deviceId: string): Promise<SepoDevice> {
    return firstValueFrom(this.deviceRepository.getDevice(deviceId));
  }

  addToPrivateTeam(deviceId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.authService.isLoggedIn()) {
        this.deviceRepository.addToPrivateTeam(deviceId).subscribe({
          next: () => resolve(),
          error: (err) => {
            this.device$.next(null);
            reject(err);
          },
        });
      } else {
        resolve();
      }
    });
  }

  async initNewDevice(deviceId: string, navigateToHome?: boolean): Promise<void> {
    await this.initDevice(deviceId);
    this.kpiLogsService.sendInitEvent(deviceId);

    try {
      await this.addToPrivateTeam(deviceId);
      const asset = await this.getDevice(deviceId);
      this.device$.next(asset);
      if (asset.redirectUrl) {
        window.open(asset.redirectUrl, '_self');
        return new Promise(() => {}); // Do not resolve app, if redirect to another url
      } else {
        this.updateLastScannedDevicesList(deviceId);
        this.warrantyService.checkWarrantyStatus(deviceId, asset.tenantId);
        if (navigateToHome) {
          const urlTree = this.router.createUrlTree(['home', deviceId]);
          this.router.navigateByUrl(urlTree);
        }
      }
    } catch (e: any) {
      this.handleDeviceErrorSnackbarAndRedirect(e, deviceId);
    }
  }

  initDevice(deviceId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const uuidRegex = /^([0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12})$/;
      if (uuidRegex.test(deviceId)) {
        this.setDeviceId(deviceId);
        resolve();
      } else {
        reject();
      }
    });
  }

  public getAssetThumbnail(assetId: string, thumbnailId: string): string {
    return this.deviceRepository.getAssetThumbnail(assetId, thumbnailId);
  }

  getLastScannedAssets(): Promise<SepoDevice[]> {
    const tenantId = this.tenantConfigService.getTenantId();
    const lastScannedAssets = localStorage.getItem(`last-scanned-assets-${tenantId}`);
    const lastScannedAssetIds = lastScannedAssets?.split(',').filter((asset) => !!asset) || [];

    return Promise.all([...lastScannedAssetIds.map((id) => this.getDeviceWithoutUpdating(id))]);
  }

  private handleDeviceErrorSnackbarAndRedirect(e: HttpErrorResponse, deviceId?: string) {
    const loggedIn = this.authService.isTokenValid();
    let navigate: string[];
    let message: string;
    let type: Notification;

    if (e.status === 401) {
      this.authService.clearToken();
      this.initNewDevice(deviceId, true);
    } else {
      switch (e.status) {
        case 403: {
          navigate = ['home'];
          message = 'SERVICEPOINT.HOME.ERROR.WRONG_TENANT';
          type = NotificationType.Error;
          break;
        }
        case 404:
        default: {
          navigate = ['qrassignment'];
          message = loggedIn ? 'SERVICEPOINT.HOME.INFO.NO_DEVICE_LOGGED_IN' : 'SERVICEPOINT.HOME.INFO.NO_DEVICE';
          type = NotificationType.Info;
          break;
        }
      }

      this.notificationService.showNotification({ message, type });
      this.router.navigate(navigate);
    }
  }

  private updateLastScannedDevicesList(assetId: string): void {
    const tenantId = this.tenantConfigService.getTenantId();
    const lastScannedAssets = localStorage.getItem(`last-scanned-assets-${tenantId}`);
    const scanned = lastScannedAssets?.split(',').filter((asset) => !!asset) || [];

    const index = scanned.indexOf(assetId);

    if (index > -1) {
      scanned.splice(index, 1);
      scanned.unshift(assetId);
    } else {
      if (scanned.length === 3) {
        scanned.pop();
      }

      scanned.unshift(assetId);
    }

    localStorage.setItem(`last-scanned-assets-${tenantId}`, scanned.join(','));
  }
}
